import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Modal } from '@arco-design/web-react';
import { produce } from 'immer';
import styled from 'styled-components';

import { LargeSelect } from '@/components/LargeSelect';

import { ReclaimPolicyFormField, isReclaimPolicyLocked, resolveReclaimPolicyValue } from '../../../components/reclaimPolicyField';
import { OptionMetaRootField } from '../../../platform/option-meta';
import { parseGiSize } from '../../../utils/diskSize';
import { usePvcOptions } from '../../shared-volume/hooks/usePvcOptions';
import {
  type VolumeItem,
  type VolumeMountItem,
  VolumeMountTab,
  VolumeMountType,
  type VolumeStorageItem,
  isVolumeStorageItem,
} from '../volumeTypes';
import { AddedPods } from './AddedPods';
import { VolumeOptionLabel } from './VolumeOptionLabel';
import { resolveVolumeMountForEdit } from './resolveVolumeMount';

const FormItem = Form.Item;

interface AddVolumeModalProps {
  namespace: string;
  open?: boolean;
  onClose?: () => void;
  onOk?: (values: VolumeStorageItem, modalFormValues: Record<string, unknown>) => void;
  data?: VolumeItem;
  containerName?: string;
  from?: string;
  /** 是否将所选 PVC 容量保存到表单卷记录，供后续卡片展示。 */
  showMountedVolumeCapacity?: boolean;
  /** SKU / 下单注入时锁定回收策略为 Delete */
  resourceEditMode?: string;
}

export function AddVolumeModal({
  namespace,
  open,
  onClose,
  onOk,
  data,
  containerName,
  from,
  showMountedVolumeCapacity,
  resourceEditMode,
}: AddVolumeModalProps) {
  const { t } = useTranslation();
  const container = containerName ?? '';
  const editData = data && isVolumeStorageItem(data) ? data : undefined;
  const { options, isLoading } = usePvcOptions(namespace, Boolean(open));
  const openInitializedRef = useRef(false);
  const reclaimLocked = isReclaimPolicyLocked(resourceEditMode);

  const initFormValues = useMemo(() => {
    const initVolumeMounts: VolumeMountItem[] = [
      {
        containerName: container,
        mountType: from === 'container-create' ? VolumeMountType.ReadWriteMany : VolumeMountType.NotMount,
        mountPath: '',
      },
    ];

    const initialValues: VolumeStorageItem = {
      type: VolumeMountTab.PERSISTENCE,
      selected: undefined as unknown as string,
      volumeName: undefined as unknown as string,
      volumeHostPath: undefined as unknown as string,
      volumeMounts: initVolumeMounts,
      reclaimPolicy: resolveReclaimPolicyValue(undefined, reclaimLocked),
    };

    if (editData) {
      return produce(editData, (draft) => {
        draft.reclaimPolicy = resolveReclaimPolicyValue(draft.reclaimPolicy, reclaimLocked);
        const existItem = resolveVolumeMountForEdit(draft.volumeMounts, container);
        draft.volumeMounts = [
          {
            containerName: container || existItem?.containerName || '',
            mountType: existItem?.mountType ?? (from === 'container-create' ? VolumeMountType.ReadWriteMany : VolumeMountType.NotMount),
            mountPath: existItem?.mountPath ?? '',
          },
        ];
      });
    }

    return initialValues;
  }, [container, editData, from, reclaimLocked]);

  const [allValues, setAllValues] = useState<Partial<VolumeStorageItem>>(initFormValues);
  const [form] = Form.useForm<VolumeStorageItem>();

  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false;
      form.resetFields();
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;

    form.setFieldsValue(initFormValues);
    setAllValues(initFormValues);
  }, [form, initFormValues, open]);

  const persistenceOptions = options.map((item) => ({
    value: item.value,
    label: (
      <VolumeOptionLabel
        iconfontName="icon-a-chijiujuanmian"
        value={item.value}
        label={t('common:persistent.volume.claim')}
        extraItems={[{ value: item.storage ?? '', label: t('common:capacity'), frozenWidth: true }]}
      />
    ),
  }));

  const handleOk = async () => {
    try {
      await form.validate();
      const formValues = form.getFieldsValue() as Partial<VolumeStorageItem>;
      const selected = formValues.selected ?? allValues.selected;
      const selectedStorage = options.find((item) => item.value === selected)?.storage;
      const nextValues = {
        ...allValues,
        ...formValues,
        type: VolumeMountTab.PERSISTENCE,
        reclaimPolicy: resolveReclaimPolicyValue(formValues.reclaimPolicy ?? allValues.reclaimPolicy, reclaimLocked),
        volumeMounts: formValues.volumeMounts ?? allValues.volumeMounts,
        ...(showMountedVolumeCapacity
          ? {
              volumeCapacity: selectedStorage
                ? parseGiSize(selectedStorage)
                : editData?.selected === selected
                  ? editData.volumeCapacity
                  : undefined,
            }
          : {}),
      } as VolumeStorageItem;
      onOk?.(nextValues, form.getFieldsValue() as Record<string, unknown>);
    } catch (error) {
      console.error('Error validating form: ', error);
    }
  };

  return (
    <StyledModal visible={open} onCancel={onClose} onOk={handleOk} title={t('common:mount.volume')}>
      <Form
        form={form}
        layout="vertical"
        initialValues={initFormValues}
        onValuesChange={(_, values) => {
          setAllValues(values as VolumeStorageItem);
        }}
      >
        <OptionMetaRootField />
        <Form.Item field="volumeName" label={t('common:volume.name')} rules={[{ required: true, message: t('common:required') }]}>
          <Input placeholder={t('common:volume.name')} />
        </Form.Item>
        <FormItem field="selected" label={t('common:persistent.volume.claim')}>
          <LargeSelect
            loading={isLoading}
            options={persistenceOptions}
            placeholderNode={
              <VolumeOptionLabel
                iconfontName="icon-a-chijiujuanmian"
                value={options.length ? t('common:please.select.persistent.volume.claim') : t('common:no.available.resources.found')}
                label={t('data-center:mount.persistent.volumes.created.from.persistent.volume.claims.to.container.instances')}
              />
            }
          />
        </FormItem>
        <AddedPods form={form} from={from} />
        <ReclaimPolicyFormField
          form={form}
          field="reclaimPolicy"
          enabled={Boolean(open)}
          lockedToDelete={reclaimLocked}
          getPopupContainer={() => document.body}
          requiredMessage={t('common:required')}
          placeholder={t('common:select.placeholder')}
          formItemStyle={{ marginTop: 16 }}
        />
      </Form>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 400px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;
